import { RadioTower } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type ExternalRestreamTarget,
  type RestreamTargetRuntimeStatus,
  RESTREAM_PLATFORM_LABELS,
  RELAY_STATUS_META,
} from '../types';

/**
 * Панель статусов рестрима на странице трансляции. Сопоставляет
 * сконфигурированные площадки (externalRestreamTargets) с runtime-статусами
 * relay (relayStatuses из DTO, обновляются через WS livestream.relay.status).
 *
 * streamKey НЕ показывается — только площадка, URL и статус.
 */

interface RestreamStatusPanelProps {
  targets: ExternalRestreamTarget[];
  statuses: RestreamTargetRuntimeStatus[];
}

export function RestreamStatusPanel({ targets, statuses }: RestreamStatusPanelProps) {
  const statusByTarget = new Map(statuses.map((s) => [s.targetId, s]));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <RadioTower className="h-4 w-4" />
          Рестрим на внешние площадки
        </CardTitle>
      </CardHeader>
      <CardContent>
        {targets.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Площадки рестрима не настроены. Добавьте их в форме редактирования
            трансляции.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Площадка</TableHead>
                <TableHead>RTMP URL</TableHead>
                <TableHead>Конфигурация</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {targets.map((target) => {
                const runtime = statusByTarget.get(target.id);
                const statusMeta = runtime
                  ? RELAY_STATUS_META[runtime.status]
                  : null;
                return (
                  <TableRow key={target.id}>
                    <TableCell className="font-medium">
                      {RESTREAM_PLATFORM_LABELS[target.platform]}
                      {target.label ? (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({target.label})
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground">
                      {target.rtmpUrl}
                    </TableCell>
                    <TableCell>
                      <Badge variant={target.enabled ? 'default' : 'secondary'}>
                        {target.enabled ? 'Включена' : 'Выключена'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {statusMeta ? (
                        <div className="flex flex-col gap-1">
                          <Badge variant={statusMeta.variant}>
                            {statusMeta.label}
                            {runtime?.restarts && runtime.restarts > 0
                              ? ` · рестартов: ${runtime.restarts}`
                              : ''}
                          </Badge>
                          {runtime?.error ? (
                            <span className="text-xs text-destructive">
                              {runtime.error}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {target.enabled ? 'не запущен' : '—'}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
